import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SignupCompleteCardProps {
  signupEmail: string;
  onBackToSignIn: () => void;
}

const SignupCompleteCard = ({ signupEmail, onBackToSignIn }: SignupCompleteCardProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleResendVerification = async () => {
    if (!signupEmail) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: signupEmail,
      });
      if (error) throw error;
      toast({
        title: "Email sent!",
        description: "Check your inbox for the verification link.",
      });
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-gradient-primary rounded-lg flex items-center justify-center">
            <Video className="w-7 h-7" />
          </div>
          <span className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            Bridge Street Auctions
          </span>
        </div>

        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold mb-2">Check Your Email</h1>
          <p className="text-muted-foreground mb-6">
            We've sent a verification link to <strong>{signupEmail}</strong>
          </p>

          <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-muted-foreground mb-2">Next steps:</p>
            <ol className="text-sm space-y-2 list-decimal list-inside">
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>Once verified, your account will be pending staff approval</li>
              <li>After approval, you can start bidding!</li>
            </ol>
          </div>

          <Button
            variant="outline"
            onClick={handleResendVerification}
            disabled={loading}
            className="w-full mb-3"
          >
            {loading ? "Sending..." : "Resend Verification Email"}
          </Button>

          <Button variant="ghost" onClick={onBackToSignIn} className="w-full">
            Back to Sign In
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SignupCompleteCard;
